// src/pages/Cirium/FlightLifecycle.jsx
import React, { useState } from 'react';
import { Card, Collapse, Tag, Row, Col, Typography, Divider } from 'antd';
import {
  ClockCircleOutlined,
  SettingOutlined,
  RocketOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';

const { Panel } = Collapse;
const { Title, Text } = Typography;

// Example data – later you can replace this with mapped statusUpdateHistory
const lifecycleData = {
  statusHistoryId: '1821-2026-01-16T06-39-11-754Z',
  stages: [
    {
      key: 'scheduled',
      label: 'Scheduled',
      color: '#1E90FF',
      icon: <ClockCircleOutlined />,
      chips: ['6 time adjustment'],
      events: [
        {
          code: 'SGP',
          label: 'Schedule',
          type: 'TIME ADJUSTMENT',
          toText: 'To: 22:00 → 22:30',
        },
        {
          code: 'ATM',
          label: 'Schedule',
          type: 'TIME ADJUSTMENT',
          toText: 'To: 2 → 3',
        },
        {
          code: 'SGA',
          label: 'Schedule',
          type: 'TIME ADJUSTMENT',
          toText: 'To: 2026‑01‑02T23:55:00.000',
        },
        {
          code: 'SGD',
          label: 'Schedule',
          type: 'TIME ADJUSTMENT',
          toText: 'To: 11 Jan 2026 22:30 → 22:30',
        },
        {
          code: 'DTM',
          label: 'Schedule',
          type: 'TIME ADJUSTMENT',
          toText: 'To: 1 → 3',
        },
      ],
    },
    {
      key: 'departure-preparation',
      label: 'Departure Preparation',
      color: '#FFC700',
      icon: <SettingOutlined />,
      chips: ['5 time adjustment', '1 equipment adjustment'],
      events: [
        {
          code: 'STD',
          label: 'STD',
          type: 'TIME ADJUSTMENT',
          toText: 'STD → 02 Jan 2026 22:35',
        },
        {
          code: 'SGA',
          label: 'SGA',
          type: 'TIME ADJUSTMENT',
          toText: 'SGA → 01:40 → 01:38',
        },
        {
          code: 'ATM',
          label: 'ATM',
          type: 'TIME ADJUSTMENT',
          toText: 'ATM → 3 → 1',
        },
      ],
    },
    {
      key: 'en-route',
      label: 'En Route',
      color: '#17D37A',
      icon: <RocketOutlined />,
      chips: ['5 en route', '4 time adjustment'],
      events: [],
    },
    {
      key: 'arrival',
      label: 'Arrival',
      color: '#7A5CFF',
      icon: <CheckCircleOutlined />,
      chips: ['2 baggage', '3 landed', '8 time adjustment'],
      events: [],
    },
  ],
};

// Small pill chip used for “6 time adjustment”, etc.
const PillChip = ({ children }) => (
  <span
    style={{
      padding: '4px 10px',
      borderRadius: 999,
      background: '#F2F4F7',
      fontSize: 12,
      color: '#4B5563',
      marginRight: 8,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

// Card for each event inside a stage (e.g. SGP / ATM / SGA blocks)
const LifecycleEventCard = ({ code, label, type, toText }) => (
  <div
    style={{
      padding: '16px',
      border: '1px solid #e2e8f0',
      borderRadius: 12,
      background: '#fff',
      marginBottom: 12,
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
      transition: 'all 0.2s',
    }}
  >
    <Row align="middle" gutter={16}>
      <Col flex="none">
        <Tag color="#dbeafe" style={{ 
          color: '#1e40af', 
          fontWeight: 700, 
          padding: '6px 12px' 
        }}>
          {code}
        </Tag>
      </Col>
      <Col flex="auto">
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 14, color: '#64748b' }}>{toText}</div>
      </Col>
      <Col flex="none">
        <Tag style={{ 
          background: '#f1f5f9', 
          color: '#475569', 
          fontSize: 12,
          fontWeight: 500 
        }}>
          {type}
        </Tag>
      </Col>
    </Row>
  </div>
);


// Main component: FlightLifecycle
const FlightLifecycle = ({ data }) => {
  const [activeKeys, setActiveKeys] = useState(
    data?.stages?.map((s) => s.key) || []
  );


  return (
  <div
    style={{
      padding: '40px 20px',
      background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
      minHeight: '100vh',
    }}
  >
    <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <Title level={2} style={{ color: '#1e293b', marginBottom: 8 }}>
          Flight Lifecycle
        </Title>
        <Text style={{ color: '#64748b', fontSize: 16 }}>
          Status History: {data?.statusHistoryId || 'Loading...'}
        </Text>
      </div>

      {/* 2 Cards Per Row - Responsive Grid */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(600px, 1fr))',
        gap: 24,
        maxWidth: '1400px',
      }}>
        {data?.stages?.map((stage, index) => (
          <div key={stage.key}>
            {/* Stage Card */}
            <Card
              hoverable
              style={{
                borderRadius: 20,
                border: `3px solid ${stage.color}20`,
                background: `linear-gradient(135deg, ${stage.color}10 0%, white 100%)`,
                height: 420,
              }}
              bodyStyle={{ padding: 24 }}
            >
              {/* Icon Header */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 16, 
                marginBottom: 20 
              }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    background: stage.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#FFFFFF',
                    fontSize: 24,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                  }}
                >
                  {stage.icon}
                </div>
                <div>
                  <div style={{ 
                    fontSize: 20, 
                    fontWeight: 700, 
                    color: '#1e293b',
                    marginBottom: 4 
                  }}>
                    {stage.label}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {stage.chips?.map((chip) => (
                      <PillChip key={chip}>{chip}</PillChip>
                    ))}
                  </div>
                </div>
              </div>

              {/* Events List */}
              {stage.events?.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  color: '#94a3b8', 
                  fontSize: 14,
                  paddingTop: 60 
                }}>
                  No events in this stage
                </div>
              ) : (
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {stage.events.map((ev) => (
                    <LifecycleEventCard
                      key={ev.code + ev.toText}
                      code={ev.code}
                      label={ev.label}
                      type={ev.type}
                      toText={ev.toText}
                    />
                  ))}
                </div>
              )}
            </Card>
          </div>
        ))}
      </div>
    </div>
  </div>
);

};

export default FlightLifecycle;
